import { core } from "../../lib/api";
import * as XLSX from "xlsx";

export type ScoutMode = "auto" | "analytics" | "build";
export interface ChartPayload {
  render: { kind: string; x?: string | null; y?: string | null; series?: string | null };
  data: { columns: string[]; rows: Record<string, unknown>[]; row_count: number; truncated: boolean; collection?: string };
}
export interface ScoutMessage { id: string; role: "user" | "assistant"; text: string; created_at: string; charts?: ChartPayload[] }
export interface ScoutSummary { conversation_id: string; title: string; mode: ScoutMode; status: string; session_id?: string; agent_id?: string; updated_at: string; pinned: boolean; account_id?: string | null }
export interface ScoutConversation extends ScoutSummary {
  messages: ScoutMessage[]; ready: boolean; error?: string; context: Record<string, unknown>;
  builder_input: Record<string, unknown>;
  build?: {
    session_id: string; status: string; agent_question?: string; error?: string; started_at?: string;
    generation?: {
      system_prompt: string; first_message: string; rationale?: string;
      tools: { name: string; why?: string; used_in?: string }[];
      variables: { key: string; description?: string; direction: string }[];
      proposed_tools?: { name: string; purpose: string }[];
      findings?: { rule: string; passed: boolean; evidence?: string; severity?: string }[];
    };
  };
}
export const scout = <T,>(path: string, init?: RequestInit) => core<T>(`/api/v1/scout${path}`, init);
export const studioScoutUrl = (conversation: ScoutSummary) => `/home/create-agent?${conversation.agent_id ? `agent_id=${encodeURIComponent(conversation.agent_id)}` : "new=1"}&scout_session_id=${encodeURIComponent(conversation.session_id || "")}&scout_conversation_id=${encodeURIComponent(conversation.conversation_id)}`;

// ─── Low-level cell helpers ───────────────────────────────────────────────────

function _csvCell(value: unknown): string {
  const text = value == null ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);
  return `"${(/^[=+@\-\t\r]/.test(text) ? "'" : "") + text.replace(/"/g, '""')}"`;
}

function _blob(content: string, type: string, filename: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a"); link.href = url; link.download = filename; link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── Legacy per-chart helpers (kept for any callers outside Scout) ─────────────

export function downloadCsv(columns: string[], rows: Record<string, unknown>[], filename = "scout-data.csv") {
  const content = [columns.map(_csvCell).join(","), ...rows.map(row => columns.map(c => _csvCell(row[c])).join(","))].join("\r\n");
  _blob("\uFEFF" + content, "text/csv;charset=utf-8", filename);
}

export function downloadXlsx(columns: string[], rows: Record<string, unknown>[], filename = "scout-data.xlsx") {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows, { header: columns });
  XLSX.utils.book_append_sheet(wb, ws, "Data");
  XLSX.writeFile(wb, filename);
}

// ─── Chart-label helper ───────────────────────────────────────────────────────

function _chartLabel(chart: ChartPayload, index: number): string {
  const { x, y, series } = chart.render;
  const candidate = y || x || series;
  return candidate ? String(candidate).replace(/[^a-zA-Z0-9 _-]/g, "").trim().slice(0, 28) || `Chart ${index + 1}` : `Chart ${index + 1}`;
}

// ─── Unified multi-chart exports ──────────────────────────────────────────────

/**
 * Download the full assistant message as a Markdown file.
 * The answer prose is kept verbatim; each chart's data is appended as a
 * fenced markdown table under a heading.
 */
export function downloadMarkdown(message: ScoutMessage, filename = "scout-report.md") {
  const charts = message.charts ?? [];
  let md = message.text.trim();

  if (charts.length) {
    md += "\n\n---\n\n## Data Tables\n";
    charts.forEach((chart, i) => {
      const label = _chartLabel(chart, i);
      const { columns, rows } = chart.data;
      md += `\n### ${label}\n\n`;
      if (!columns.length || !rows.length) {
        md += "_No data._\n";
      } else {
        const header = `| ${columns.join(" | ")} |`;
        const sep = `| ${columns.map(() => "---").join(" | ")} |`;
        const body = rows.map(r =>
          `| ${columns.map(c => String(r[c] ?? "—").replace(/\|/g, "\\|")).join(" | ")} |`
        ).join("\n");
        md += [header, sep, body].join("\n") + "\n";
      }
    });
  }

  _blob(md, "text/markdown;charset=utf-8", filename);
}

/**
 * Download the full answer as a PDF using jspdf + html2canvas.
 * Renders a hidden styled DOM node, captures it to canvas page by page,
 * then writes a real .pdf file directly to downloads — no dialog, no popup.
 */
export async function downloadPdf(message: ScoutMessage, reportTitle = "Scout Report", filename = "scout-report.pdf") {
  const charts = message.charts ?? [];

  // ── 1. Build styled HTML content ──────────────────────────────────────────
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Convert markdown prose to light HTML
  const proseHtml = message.text
    .replace(/^(#{1,6}) (.+)$/gm, (_: string, hashes: string, t: string) => {
      const level = Math.min(hashes.length + 1, 6);
      return `<h${level}>${esc(t)}</h${level}>`;
    })
    .replace(/\*\*(.+?)\*\*/g, (_: string, t: string) => `<strong>${esc(t)}</strong>`)
    .replace(/\*(.+?)\*/g, (_: string, t: string) => `<em>${esc(t)}</em>`)
    .replace(/`([^`]+)`/g, (_: string, t: string) => `<code>${esc(t)}</code>`)
    // Markdown table rows — skip pure-separator lines (---|---|---)
    .replace(/^\|(.+)\|$/gm, (line: string) => {
      if (/^\|[\s|:-]+\|$/.test(line)) return "";
      const cells = line.split("|").slice(1, -1).map(c => c.trim());
      return `<tr>${cells.map(c => `<td>${c}</td>`).join("")}</tr>`;
    })
    .replace(/(<tr>[\s\S]*?<\/tr>\n?)+/g, (block: string) => `<table>${block}</table>`)
    .replace(/^[-*] (.+)$/gm, (_: string, t: string) => `<li>${esc(t)}</li>`)
    .replace(/(<li>[\s\S]*?<\/li>\n?)+/g, (block: string) => `<ul>${block}</ul>`)
    .replace(/\n{2,}/g, "<br/><br/>");

  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);

  // Capture rendered chart nodes from the DOM (they have id scout-chart-{msg.id}-{i})
  const chartImages = await Promise.all(charts.map(async (_, i) => {
    const el = document.getElementById(`scout-chart-${message.id}-${i}`);
    if (!el) return null;
    try {
      // Temporarily expand height if it's scrollable? Recharts takes full width/height
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      return canvas.toDataURL("image/jpeg", 0.95);
    } catch {
      return null;
    }
  }));

  // Chart data tables HTML + injected chart snapshots
  const chartTablesHtml = charts.map((chart, i) => {
    const label = _chartLabel(chart, i);
    const imgHtml = chartImages[i] ? `<img src="${chartImages[i]}" style="width:100%;height:auto;margin:12px 0;border-radius:8px;border:1px solid #eee;" />` : "";
    const { columns, rows } = chart.data;
    if (!columns.length || !rows.length) return `<h3>${esc(label)}</h3>${imgHtml}<p><em>No data.</em></p>`;
    const thead = `<tr>${columns.map(c => `<th>${esc(c)}</th>`).join("")}</tr>`;
    const tbody = rows.map(r =>
      `<tr>${columns.map(c => `<td>${esc(String(r[c] ?? "—"))}</td>`).join("")}</tr>`
    ).join("");
    return `<h3>${esc(label)}</h3>${imgHtml}<table><thead>${thead}</thead><tbody>${tbody}</tbody></table>`;
  }).join("\n");

  const fullHtml = `
    <div id="pdf-root" style="
      font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
      font-size:12px;color:#111;padding:40px;width:760px;
      line-height:1.6;box-sizing:border-box;background:#fff;
    ">
      <h1 style="font-size:20px;margin:0 0 4px">${esc(reportTitle)}</h1>
      <p style="color:#666;font-size:10px;margin:0 0 24px">${new Date().toLocaleString()}</p>
      <style>
        #pdf-root h2{font-size:15px;margin:20px 0 6px;border-bottom:1px solid #ddd;padding-bottom:4px}
        #pdf-root h3{font-size:13px;margin:16px 0 4px;color:#333}
        #pdf-root h4,#pdf-root h5,#pdf-root h6{font-size:12px;margin:12px 0 4px}
        #pdf-root table{border-collapse:collapse;width:100%;margin:8px 0 16px;font-size:10px;page-break-inside:auto}
        #pdf-root tr{page-break-inside:avoid;page-break-after:auto}
        #pdf-root th{background:#f0f0f0;border:1px solid #ccc;padding:4px 8px;text-align:left;font-weight:600}
        #pdf-root td{border:1px solid #ddd;padding:4px 8px;vertical-align:top}
        #pdf-root tr:nth-child(even) td{background:#fafafa}
        #pdf-root ul{margin:4px 0 8px 20px;padding:0}
        #pdf-root li{margin:2px 0}
        #pdf-root code{background:#f5f5f5;padding:1px 4px;border-radius:3px;font-size:10px;font-family:monospace}
        #pdf-root hr{border:none;border-top:1px solid #ddd;margin:20px 0}
      </style>
      <div>${proseHtml}</div>
      ${chartTablesHtml ? `<hr/><h2>Data Tables</h2>${chartTablesHtml}` : ""}
    </div>`;

  // ── 2. Mount hidden container ──────────────────────────────────────────────
  const container = document.createElement("div");
  container.style.cssText = "position:fixed;left:-9999px;top:0;z-index:-1;background:#fff";
  container.innerHTML = fullHtml;
  document.body.appendChild(container);
  const root = container.querySelector("#pdf-root") as HTMLElement;

  try {
    // ── 3. Capture with html2canvas + write to single continuous PDF page ─
    const A4_W_PX = 794;
    const scale = 2; // retina sharpness

    // Ensure the container is rendered fully before capturing
    await new Promise(r => setTimeout(r, 100));

    const canvas = await html2canvas(root, {
      scale,
      useCORS: true,
      backgroundColor: "#ffffff",
      width: A4_W_PX,
      windowWidth: A4_W_PX,
    });

    const pdfW = A4_W_PX;
    const pdfH = canvas.height / scale;
    
    // Create a PDF with custom dimensions matching the exact content height
    const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: [pdfW, pdfH], compress: true });
    
    const imgData = canvas.toDataURL("image/jpeg", 0.95);
    pdf.addImage(imgData, "JPEG", 0, 0, pdfW, pdfH);
    pdf.save(filename);
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Download all chart datasets as a single XLSX workbook.
 * Sheet 1 = the answer prose. Sheets 2-N = one per chart, named by label.
 */
export function downloadFullXlsx(message: ScoutMessage, filename = "scout-report.xlsx") {
  const charts = message.charts ?? [];
  const wb = XLSX.utils.book_new();

  // Summary sheet — answer text split into rows
  const summaryRows = message.text.split("\n").map(line => [line]);
  const summaryWs = XLSX.utils.aoa_to_sheet([["Report"], ...summaryRows]);
  XLSX.utils.book_append_sheet(wb, summaryWs, "Report");

  // One sheet per chart
  const seen: Record<string, number> = {};
  charts.forEach((chart, i) => {
    const base = _chartLabel(chart, i);
    seen[base] = (seen[base] ?? 0) + 1;
    const sheetName = seen[base] === 1 ? base : `${base.slice(0, 26)} (${seen[base]})`;
    const { columns, rows } = chart.data;
    if (!columns.length) return;
    const ws = XLSX.utils.json_to_sheet(rows, { header: columns });
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  XLSX.writeFile(wb, filename);
}

/**
 * Download all chart datasets as a ZIP of CSV files.
 * One CSV per chart, named by label, plus a summary.csv with the answer prose.
 * Mirrors the server-side pattern used in reports/export.py.
 */
export async function downloadFullCsv(message: ScoutMessage, filename = "scout-report.zip") {
  const charts = message.charts ?? [];

  // Dynamically import JSZip — it ships with the xlsx bundle indirectly;
  // if not available, fall back to a single merged CSV.
  try {
    const { default: JSZip } = await import("jszip");
    const zip = new JSZip();

    // summary.txt — the markdown answer
    zip.file("summary.md", message.text);

    const seen: Record<string, number> = {};
    charts.forEach((chart, i) => {
      const base = _chartLabel(chart, i);
      seen[base] = (seen[base] ?? 0) + 1;
      const name = seen[base] === 1 ? base : `${base} (${seen[base]})`;
      const { columns, rows } = chart.data;
      if (!columns.length) return;
      const lines = [
        columns.map(_csvCell).join(","),
        ...rows.map(r => columns.map(c => _csvCell(r[c])).join(",")),
      ].join("\r\n");
      zip.file(`${name}.csv`, "\uFEFF" + lines);
    });

    const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a"); link.href = url; link.download = filename; link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    // JSZip not available — fall back to a flat merged CSV with a chart_label column
    const allColumns = Array.from(new Set(charts.flatMap(c => c.data.columns)));
    const allColumns_ = ["chart", ...allColumns];
    const allRows = charts.flatMap((chart, i) => {
      const label = _chartLabel(chart, i);
      return chart.data.rows.map(r => ({ chart: label, ...Object.fromEntries(allColumns.map(c => [c, r[c] ?? ""])) }));
    });
    downloadCsv(allColumns_, allRows, filename.replace(".zip", ".csv"));
  }
}

